import { copyText } from '../copied'
import { api, dismiss, getQueue, mutate, mutateUpload, type Ack } from '../api'
import { sheet, shrink } from '../day-close'
import { applySavedBusinessRow, businessSettings, hasBusinessSettings, setInvoicesOn } from '../business-settings'
import { customerInvoiceView } from '../invoices'
import '../invoices.css'
import { lineDrawing } from '../picture-tiles'
import { workTitle } from '../trade'
import { loadMe } from '../me'
import { addJobHintDue, guideSeen, markGuideSeen } from '../feature-guide'
import { routeGen } from '../route-state'
import { clear, el, loadError, money, parseMoney, toast, todayISO } from '../ui'
import { currencySymbol, currencyWord, screenLocale } from '../round-config'
import { PHONE_PATH, phoneCard, type PhoneRow } from '../phone-card'
import { WA_PATH, whatsappCard, type WaCard } from '../whatsapp-card'
import { remindersRow } from '../reminders'
import { nudgesRow } from '../timer-nudges'

// Extra features (owner, Liam 2 Oct 2026: "why is it not on the app for customers to turn it on in an extra features
// section?"): the things an owner can switch on for their own round. Reached from Your business or the owner More hub (#/extra-features);
// workers and the admin never land here (main.ts route()).
//
// Several jobs per customer and quotes by email, with nothing listed that cannot be turned on yet (Liam: no
// placeholders). Turning it on asks first, in the words of the Rounds screen's confirm; once any customer has a second
// job, or a customer's first job is stopped, it stays on and no button is offered, the server's own rule
// (functions/api/business/several-jobs.ts). After the switch the app reloads /api/me: the screens are drawn again for
// the round as it is now, or, with a photo or voice note still to send, once it has gone (me.ts followIdentity).
//
// The switch can land long after the tap (no signal: it waits in the queue, and may send after the owner has left
// this page or reopened the app). Wherever it lands, /api/me is asked again (below), so "Add another job" appears
// without the owner doing anything. A late answer never draws on a screen the owner has moved to (Astra r1).

const PATH = '/api/business/several-jobs'
const QUOTES_PATH = '/api/business/quotes-email'
interface QuotesSwitch { id: number; quotes_by_email: boolean; quotes_address: string | null; has_prices: boolean; version: number }
let quotesSwitch: QuotesSwitch | null = null
// Calls and texts through Roundly (0059): this business's own Twilio connection, or null while it has not loaded.
let phoneRow: PhoneRow | null = null
// WhatsApp from Roundly on Meta's platform (0081, src/whatsapp-card.ts): the card, or null while it has not loaded.
let waCard: WaCard | null = null
// Invoices to customers (0065): the switch and the set-up, off as standard (Liam, 12 Oct 2026).
const INVOICES_PATH = '/api/business/invoices'
interface InvoiceSetup { on: boolean; address: string | null; contact: string | null; has_logo: boolean; prefix: string; next: number; next_number: string; pay_days: number; vat_on: boolean; vat_number: string | null; vat_rate_bp: number; vat_allowed: boolean; has_bank: boolean; email_on: boolean; version: number }
let invoiceSetup: InvoiceSetup | null = null
// Tiers from job time (0070): the round's own cut-offs in pence a minute, highest first; null is off.
const TIERS_PATH = '/api/business/tiers'
interface TiersSetup { cutoffs: number[] | null; version: number; figured: number; by_hand: number }
let tiersSetup: TiersSetup | null = null
// Several jobs as last read, so a save on another card can hand it the new version (carrySettingsVersion).
let severalJobs: SeveralJobsSwitch | null = null
// Working with others (0078; Liam, 19 Oct 2026: "Surely we do this as part of the onboarding", sign-up's "Just me" sets
// it the same way): On for a round with a team, as every round from before is; Off is the simpler app for one person.
// Stored the other way round (works_alone). Turning it off asks "Just you on this round?" and goes only while the round
// really is one person, the server's own rule (functions/lib/works-alone.ts), whose reason the card shows; turning it on
// asks too. Last of the cards, and not in Get set up. After the switch the app reloads /api/me, as for several jobs.
const ALONE_PATH = '/api/business/works-alone'
interface AloneSwitch { id: number; works_alone: boolean; version: number; can_go_alone: boolean; not_alone_because: string | null }
let aloneSwitch: AloneSwitch | null = null
// Parking notes (0084; Liam, 22 Oct 2026, design A: "this feature should be Off by standard, it can go in extra features
// that you can turn on"): "+ Add parking or times" at the foot of a Today card's Access & notes, owner only. Read and saved
// with Your business's own settings (GET/PATCH /api/business), on the round's one settings version.
const BUSINESS_PATH = '/api/business'
interface ParkingSwitch { on: boolean; version: number }
let parkingSwitch: ParkingSwitch | null = null

// Several jobs, Quotes by email, Invoices and Working with others are all saved against the round's one settings
// version; Tiers has its own. Two of them tapped close together went on the same version and the second was refused as
// "changed on another phone" against the owner's own first tap (Liam's notes, 15 Oct 2026: "often not saving").
const SETTINGS_PATHS = [PATH, QUOTES_PATH, INVOICES_PATH, ALONE_PATH, BUSINESS_PATH]

// One of them saved: the others take its new version at once, so a tap on the next card before the screen has read
// everything again goes on the version just saved, not the one before it. Only a card read on the very version that
// save was made on, and only for a save that moved it on by one: a card read before a change on another phone stays
// behind and is refused, never handed a version that would let its old values overwrite that change (Codex).
function carrySettingsVersion(from: unknown, row: unknown): void {
  const v = (row as { version?: unknown } | null | undefined)?.version
  if (typeof from !== 'number' || v !== from + 1) return
  for (const s of [severalJobs, quotesSwitch, invoiceSetup, aloneSwitch, parkingSwitch]) if (s && s.version === from) s.version = v
}

// A change still on its way (sending, or waiting for signal). A tap now would go on the version before it, so it is
// held and said plainly; the owner taps again once it has saved. Refused and parked changes are not on their way.
function stillSaving(paths: string[], note: HTMLElement): boolean {
  if (!busy && !getQueue().some((q) => paths.includes(q.path) && q.status !== 'conflict' && q.status !== 'failed')) return false
  note.textContent = STILL_SAVING
  return true
}
const STILL_SAVING = 'Still saving your last change. Try again in a moment.'
// Liam's first answer (13 Oct 2026), changeable: £1.50, £1 and 75p a minute.
const STARTING_CUTOFFS = [150, 100, 75]

export type StaysOnBecause = 'second_job' | 'stopped_first_job' | null

export interface SeveralJobsSwitch {
  id: number
  several_jobs: boolean
  version: number
  in_use: boolean
  stays_on_because?: StaysOnBecause
}

let requestSequence = 0
let busy = false
// Arrived from "Set up invoices" (#/extra-features?show=invoices): the page is brought to the Invoices card and its
// switch glows, once; Several jobs' first-time glow stays off for that visit so it never points at the wrong switch.
let arrivedFor: 'invoices' | null = null
let showPending = false
// Invoice details just saved (design B, Liam, 25 Oct 2026: "Go with B"; https://claude.ai/artifact/PSHHDk3d7yF2Lukm2ryqDc;
// he tapped Save and nothing said it had saved): Save turns to "Saved" with a tick, the card glows lemon once, and What
// next? offers Make an invoice and See how it looks. Typing in any box brings Save back; opening the page again clears it.
let invoicesSaved = false
let invoicesGlow = false
// The page has drawn every card since it opened. On opening each card shows as its answer comes; after that, a read
// again (a switch, a save) waits for every answer and draws once. Drawn card by card, the page was one card tall for a
// moment and the phone lost its place (Liam, 23 Oct 2026: "Turning this off and on jumps me back up the page").
let drawnWhole = false
// Between the tap that slides the switch on and the owner's answer to the confirm.
let asking = false
// How long the knob takes to slide across before the confirm rises (styles.css .feature-switch).
const SLIDE_MS = 160

// A queued switch landing from the retry queue (api.ts), whichever screen is on show: the app learns it from /api/me.
if (typeof window !== 'undefined') {
  window.addEventListener('roundly:queued-landed', (e) => {
    const d = (e as CustomEvent<{ method?: string; path?: string }>).detail
    if (d?.method === 'PATCH' && (d.path === PATH || d.path === QUOTES_PATH || d.path === ALONE_PATH)) void loadMe()
    // Invoices or Tiers saved once there was signal: the cards are read again, so the next tap goes on its version.
    else if (d?.method === 'PATCH' && (d.path === INVOICES_PATH || d.path === TIERS_PATH) && !busy && window.location.hash.startsWith('#/extra-features')) void load()
  })
}

export const CONFIRM_WORDS = [
  'Your customers can then have Gutters, Windows and other jobs, each with its own price and how often.',
  "You can't turn it off again once any customer has a second job, or once you stop a customer's first job.",
]

// Working with others' confirms (Liam, 19 Oct 2026): what changes, in plain words, before anything is sent.
export const ALONE_TITLE = 'Just you on this round?'
export const ALONE_WORDS = [
  'Roundly leaves out what only a team needs: who is out each day, "Who\'s doing it?" when you book, and worker pay.',
  'The days Messages offers come from My working days.',
  'Your customers, visits and money stay as they are. You can turn this back on here.',
]
export const WITH_OTHERS_TITLE = 'Show who is out each day, who does each visit and worker pay?'
export const WITH_OTHERS_WORDS = [
  'The Diary shows who is out each day, Book a visit asks who is doing it, and Money shows worker pay.',
  'Your working days stay yours. Give a day to someone else in the diary.',
]

// Why it stays on, on the card.
export function staysOnWords(why: StaysOnBecause | undefined): string {
  if (why === 'stopped_first_job') return "A customer's first job is stopped, so this stays on."
  return 'Some of your customers have a second job, so this stays on.'
}

// What the feature shows for where the switch is: Off with Turn on; On with Turn off while it can still go off; On and
// "stays on" with no button once a customer has a second job or a stopped first job.
export function switchView(s: Pick<SeveralJobsSwitch, 'several_jobs' | 'in_use'>): { status: 'On' | 'Off'; button: 'Turn on' | 'Turn off' | null; staysOn: boolean } {
  if (!s.several_jobs) return { status: 'Off', button: 'Turn on', staysOn: false }
  if (s.in_use) return { status: 'On', button: null, staysOn: true }
  return { status: 'On', button: 'Turn off', staysOn: false }
}

// The confirm before it goes on: a sheet of its own, so nothing is sent until the owner says yes. Not now, Escape or
// tapping outside is a no, and the switch slides back. Several jobs' words unless told others (Working with others).
interface ConfirmAsk { title: string; words: readonly string[]; yes: string; icon: 'jobs' | 'people' }
const SEVERAL_JOBS_ASK: ConfirmAsk = { title: 'Turn on several jobs?', words: CONFIRM_WORDS, yes: 'Turn on', icon: 'jobs' }
function confirmTurnOn(onYes: () => void, onNo: () => void, over: HTMLElement | null = null, ask: ConfirmAsk = SEVERAL_JOBS_ASK): HTMLDialogElement {
  const dialog = el('dialog', { class: 'feature-confirm', 'aria-labelledby': 'feature-confirm-title' }) as HTMLDialogElement
  const yes = el('button', { type: 'button', class: 'btn-primary' }, ask.yes)
  const no = el('button', { type: 'button', class: 'btn-secondary' }, 'Not now')
  let said = false
  dialog.append(
    featureIcon('feature-confirm-icon', ask.icon),
    el('h2', { id: 'feature-confirm-title' }, ask.title),
    ...ask.words.map((w) => el('p', {}, w)),
    el('div', { class: 'feature-confirm-actions' }, no, yes),
  )
  no.addEventListener('click', () => dialog.close())
  yes.addEventListener('click', () => {
    said = true
    dialog.close()
    onYes()
  })
  dialog.addEventListener('click', (e) => {
    if ((e as MouseEvent).target === dialog) dialog.close() // a tap on the dimmed page around it
  })
  dialog.addEventListener('close', () => {
    dialog.remove()
    if (!said) onNo()
  })
  document.body.append(dialog)
  dialog.showModal()
  // It opens over the feature card, edge to edge with it, at every width, rather than floating in the middle of the
  // window apart from what it asks about.
  const place = (): void => {
    if (!over || !over.isConnected || typeof over.getBoundingClientRect !== 'function') return
    const r = over.getBoundingClientRect()
    if (!r.width) return
    const h = dialog.offsetHeight || 0
    const top = Math.max(12, Math.min(r.top, window.innerHeight - h - 12))
    dialog.style.margin = '0'
    dialog.style.left = `${Math.round(r.left)}px`
    dialog.style.top = `${Math.round(top)}px`
    dialog.style.width = `${Math.round(r.width)}px`
  }
  place()
  window.addEventListener('resize', place)
  dialog.addEventListener('close', () => window.removeEventListener('resize', place))
  return dialog
}

// ---- pictures ----------------------------------------------------------------------------------------------------

const SVG = 'http://www.w3.org/2000/svg'
function svgEl(tag: string, attrs: Record<string, string>): SVGElement {
  const node = document.createElementNS(SVG, tag) as SVGElement
  for (const [k, v] of Object.entries(attrs)) node.setAttribute(k, v)
  return node
}

// A small drawn icon: 'switch' (a switch with a spark, for Extra features as a whole), 'jobs' (two job rows and a
// plus, for several jobs per customer) or 'people' (two people, for Working with others). Decoration only.
export function featureIcon(className: string, kind: 'switch' | 'jobs' | 'mail' | 'people' = 'switch'): HTMLElement {
  const svg = svgEl('svg', { viewBox: '0 0 32 32', width: '32', height: '32', 'aria-hidden': 'true', focusable: 'false' })
  const ink = 'currentColor'
  if (kind === 'switch') {
    svg.append(
      svgEl('rect', { x: '3', y: '11', width: '22', height: '13', rx: '6.5', fill: 'none', stroke: ink, 'stroke-width': '2.2' }),
      svgEl('circle', { cx: '18.5', cy: '17.5', r: '4', fill: ink }),
      svgEl('path', { d: 'M26 3v6M23 6h6', stroke: ink, 'stroke-width': '2.2', 'stroke-linecap': 'round' }),
    )
  } else if (kind === 'people') {
    svg.append(
      svgEl('circle', { cx: '12', cy: '10', r: '4.5', fill: 'none', stroke: ink, 'stroke-width': '2.2' }),
      svgEl('path', { d: 'M3.5 26c.8-5 4.2-7.5 8.5-7.5s7.7 2.5 8.5 7.5', fill: 'none', stroke: ink, 'stroke-width': '2.2', 'stroke-linecap': 'round' }),
      svgEl('path', { d: 'M20 5.8a4.2 4.2 0 0 1 0 8.4M23.5 18.8c2.6.9 4.4 3.2 5 6.7', fill: 'none', stroke: ink, 'stroke-width': '2.2', 'stroke-linecap': 'round' }),
    )
  } else if (kind === 'mail') {
    svg.append(
      svgEl('rect', { x: '4', y: '8', width: '24', height: '17', rx: '3', fill: 'none', stroke: ink, 'stroke-width': '2.2' }),
      svgEl('path', { d: 'M5 10l11 8 11-8', fill: 'none', stroke: ink, 'stroke-width': '2.2', 'stroke-linecap': 'round', 'stroke-linejoin': 'round' }),
    )
  } else {
    svg.append(
      svgEl('rect', { x: '4', y: '5', width: '18', height: '7', rx: '2', fill: 'none', stroke: ink, 'stroke-width': '2.2' }),
      svgEl('rect', { x: '4', y: '16', width: '18', height: '7', rx: '2', fill: 'none', stroke: ink, 'stroke-width': '2.2' }),
      svgEl('path', { d: 'M26.5 19v8M22.5 23h8', stroke: ink, 'stroke-width': '2.4', 'stroke-linecap': 'round' }),
    )
  }
  return el('span', { class: className }, svg)
}

// A small padlock beside "this stays on".
function lockIcon(): HTMLElement {
  const svg = svgEl('svg', { viewBox: '0 0 24 24', width: '18', height: '18', 'aria-hidden': 'true', focusable: 'false' })
  svg.append(
    svgEl('rect', { x: '5', y: '11', width: '14', height: '10', rx: '2', fill: 'none', stroke: 'currentColor', 'stroke-width': '2' }),
    svgEl('path', { d: 'M8 11V8a4 4 0 0 1 8 0v3', fill: 'none', stroke: 'currentColor', 'stroke-width': '2', 'stroke-linecap': 'round' }),
  )
  return el('span', { class: 'feature-lock' }, svg)
}

// What a customer page looks like once it is on, drawn small: the price tile with "Add another job" under it, and a
// second job beside it.
function miniCustomer(on: boolean): HTMLElement {
  return el(
    'div',
    { class: `xf-mini${on ? ' xf-mini-on' : ''}`, 'aria-hidden': 'true' },
    el('span', { class: 'xf-mini-name' }, 'Example customer'),
    el(
      'span',
      { class: 'xf-mini-tiles' },
      el('span', { class: 'xf-mini-tile' }, el('span', { class: 'xf-mini-label' }, 'PRICE'), el('span', { class: 'xf-mini-value' }, money(2000)), el('span', { class: 'xf-mini-add' }, '+ Add another job')),
      el('span', { class: 'xf-mini-tile xf-mini-job' }, el('span', { class: 'xf-mini-label' }, 'GUTTERS'), el('span', { class: 'xf-mini-value' }, money(6000)), el('span', { class: 'xf-mini-often' }, 'every 26 weeks')),
    ),
  )
}

// A refusal this page explains itself is not left in the saving indicator as "needs review".
function letGo(): void {
  for (const q of getQueue()) if ((q.status === 'conflict' || q.status === 'failed') && q.path === PATH) dismiss(q.key)
}

function failureWords(ack: Ack<unknown>): string {
  if (ack.code === 'jobs_in_use') return ack.error ?? 'Several jobs stays on.'
  if (ack.status === 409) return 'This was changed on another phone. Showing it as it is now.'
  if (ack.signedOut) return 'Your sign-in has expired. Sign in again and try once more.'
  if (ack.status >= 400 && ack.status < 500) return ack.error ?? 'That did not work. Try again.'
  return 'No signal. It will change once the connection is back.'
}

// The switch: the app's own (People's "Show job prices"), in Roundly yellow when on, with a tick in the knob.
function switchButton(on: boolean, locked: boolean): HTMLButtonElement {
  const b = el(
    'button',
    { type: 'button', role: 'switch', class: `mb-switch feature-switch${on ? ' mb-switch-on' : ''}`, 'aria-checked': on ? 'true' : 'false', 'aria-labelledby': 'feature-several-jobs' },
    el('span', { class: 'mb-switch-knob' }, el('span', { class: 'feature-tick', 'aria-hidden': 'true' }, '✓')),
  ) as HTMLButtonElement
  if (locked) {
    b.disabled = true
    b.setAttribute('aria-describedby', 'feature-stays')
  }
  return b
}

function slide(b: HTMLElement, on: boolean): void {
  b.classList.toggle('mb-switch-on', on)
  b.setAttribute('aria-checked', on ? 'true' : 'false')
}

function render(s: SeveralJobsSwitch, root: HTMLElement, note = '', pop = false): void {
  clear(root)
  const heading = el('div', { class: 'page-heading' })
  heading.append(
    el(
      'div',
      {},
      el('h2', {}, 'Extra features'),
      el('p', { class: 'muted' }, 'Choose the tools your round needs. Existing jobs and history are always kept.'),
    ),
  )
  root.append(heading)

  const view = switchView(s)
  const layout = el('div', { class: 'xf-layout' })
  const card = el('section', { class: `feature-card${s.several_jobs ? ' feature-card-on' : ''}`, 'aria-labelledby': 'feature-several-jobs' })
  const sw = switchButton(s.several_jobs, view.staysOn)
  const status = el('span', { class: 'feature-status' }, view.status)
  card.append(
    el(
      'div',
      { class: 'feature-top' },
      featureIcon('feature-icon', 'jobs'),
      el(
        'div',
        { class: 'feature-titles' },
        el('h3', { id: 'feature-several-jobs' }, 'Several jobs per customer'),
        el('p', {}, 'Gutters, conservatory roofs and other jobs, each with its own price, how often and next clean.'),
      ),
      el('div', { class: 'feature-control' }, sw, status),
    ),
    el(
      'div',
      { class: 'feature-preview' },
      el('p', { class: 'feature-see' }, el('strong', {}, "What you'll see: "), '"Add another job" under a customer\'s price.'),
      miniCustomer(s.several_jobs),
    ),
  )
  card.append(el('div', { class: 'feature-where' }, el('strong', {}, 'Where it appears'), el('span', {}, 'On each customer, under their price. Several jobs also appear in Diary, Messages and Owed.'), el('br'), el('a', { href: '#/grid' }, 'Open Work ›')))
  if (view.staysOn) card.append(el('p', { class: 'feature-stays', id: 'feature-stays' }, lockIcon(), el('span', {}, staysOnWords(s.stays_on_because))))
  // Switched on: the next step, until the owner has seen "Add another job" on a customer (feature-guide.ts).
  if (s.several_jobs && addJobHintDue()) {
    card.append(
      el(
        'div',
        { class: 'guide-hint feature-next', role: 'status' },
        el('span', {}, 'Now open a customer and tap Add another job'),
        el('a', { class: 'feature-next-go', href: '#/grid' }, 'Open customers ›'),
      ),
    )
  }
  const error = el('p', { class: 'form-error', role: 'alert' }, note)
  card.append(error)

  // The switch glows until it has been used once (feature-guide.ts).
  if (!guideSeen('switch') && !s.several_jobs && arrivedFor !== 'invoices') sw.classList.add('guide-glow')
  if (pop) sw.classList.add('feature-switch-pop')
  if (view.button) {
    const turnOn = !s.several_jobs
    sw.addEventListener('click', () => {
      if (stillSaving(SETTINGS_PATHS, error)) return
      sw.classList.remove('guide-glow')
      markGuideSeen('switch')
      if (turnOn) {
        if (asking) return
        asking = true
        // It moves under the finger, and the confirm rises once the knob has landed; the confirm decides whether it
        // stays.
        slide(sw, true)
        const still = typeof window.matchMedia === 'function' && window.matchMedia('(prefers-reduced-motion: reduce)').matches
        window.setTimeout(() => {
          confirmTurnOn(
            () => {
              asking = false
              void flip(s, true, root)
            },
            () => {
              asking = false
              slide(sw, false)
            },
            card,
          )
        }, still ? 0 : SLIDE_MS)
      } else {
        slide(sw, false)
        void flip(s, false, root)
      }
    })
  }

  const aside = el(
    'aside',
    { class: 'xf-aside' },
    el('h3', {}, 'How extra features work'),
    el(
      'ul',
      {},
      el('li', {}, 'Each one adds to your screens only once you turn it on.'),
      el('li', {}, 'Your customers, visits and money stay as they are when you turn a feature on.'),
      el('li', {}, 'Several jobs can be turned off until a customer has a second job or a stopped first job.'),
    ),
  )
  const cards = el('div', { class: 'xf-cards' }, card)
  if (quotesSwitch) cards.append(quotesCard(quotesSwitch, root))
  if (waCard && (waCard.offered || waCard.connection)) cards.append(whatsappCard(waCard, () => void load()))
  if (phoneRow) cards.append(phoneCard(phoneRow, () => void load(), { metaWhatsApp: !!waCard?.connection }))
  if (invoiceSetup) cards.append(invoicesCard(invoiceSetup, root))
  if (tiersSetup) cards.append(tiersCard(tiersSetup, root))
  if (parkingSwitch) cards.append(parkingCard(parkingSwitch, root))
  if (aloneSwitch) cards.append(aloneCard(aloneSwitch, root))
  // This phone: Job reminders and Timer nudges, moved here from the foot of Today for an owner (Liam, 2 Nov 2026: "Not
  // sure these should go here?"). Both are set once and belong to this phone, not the round. Shown only when one is.
  const reminders = remindersRow()
  const nudges = nudgesRow()
  if (reminders || nudges) {
    const phone = el('section', { class: 'xf-this-phone', 'aria-labelledby': 'xf-this-phone' }, el('h3', { id: 'xf-this-phone' }, 'This phone'))
    if (reminders) phone.append(reminders)
    if (nudges) phone.append(nudges)
    cards.append(phone)
  }
  layout.append(cards, aside)
  root.append(layout)
}

function showInvoicesCard(root: HTMLElement): void {
  const card = root.querySelector<HTMLElement>('[aria-labelledby="feature-invoices"]')
  if (!card) return
  const still = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches
  if (typeof requestAnimationFrame !== 'function' || typeof window.scrollTo !== 'function') return
  requestAnimationFrame(() => {
    if (!card.isConnected) return
    const top = document.querySelector<HTMLElement>('header.topbar')?.getBoundingClientRect().bottom ?? 0
    window.scrollTo({ top: Math.max(0, window.scrollY + card.getBoundingClientRect().top - top - 12), behavior: still ? 'auto' : 'smooth' })
  })
}

// How long the switch's pop plays before the app asks /api/me again (which may draw every screen afresh).
const POP_MS = 180

async function flip(s: SeveralJobsSwitch, on: boolean, root: HTMLElement): Promise<void> {
  if (busy) return
  // The screen this tap was made on: an answer arriving after the owner has moved on draws nothing.
  const gen = routeGen
  busy = true
  for (const b of root.querySelectorAll<HTMLButtonElement>('.feature-switch')) b.disabled = true
  const body: Record<string, unknown> = { on, version: s.version }
  if (on) body.confirmed = true
  const ack = await mutate<SeveralJobsSwitch>({ label: on ? 'Turn on several jobs' : 'Turn off several jobs', method: 'PATCH', path: PATH, body })
  busy = false
  if (ack.ok) carrySettingsVersion(body.version, ack.row)
  if (ack.ok && on) markGuideSeen('switched-on')
  // The rest of the app learns the switch from /api/me, and draws its screens again (me.ts), wherever the owner is now;
  // after the switch's pop, so the owner sees it land.
  if (ack.ok) window.setTimeout(() => void loadMe(), POP_MS)
  if (ack.status >= 400 && ack.status < 500) letGo()
  if (gen !== routeGen) return
  if (ack.ok) {
    void load('', on)
    return
  }
  const words = failureWords(ack)
  if (ack.status >= 400 && ack.status < 500) {
    void load(words)
    return
  }
  render(s, root, words)
}

async function load(note = '', pop = false): Promise<void> {
  const gen = routeGen
  const sequence = ++requestSequence
  const root = document.querySelector<HTMLElement>('#screen')!
  try {
    const quotesRequest = api<{ row: QuotesSwitch }>(QUOTES_PATH).catch(() => null)
    const waRequest = api<{ row: WaCard }>(WA_PATH).catch(() => null)
    const phoneRequest = api<{ row: PhoneRow }>(PHONE_PATH).catch(() => null)
    const invoicesRequest = api<{ row: InvoiceSetup }>(INVOICES_PATH).catch(() => null)
    const tiersRequest = api<{ row: TiersSetup }>(TIERS_PATH).catch(() => null)
    const aloneRequest = api<{ row: AloneSwitch }>(ALONE_PATH).catch(() => null)
    const parkingRequest = api<{ settings: { parking_notes?: boolean }; version: number }>(BUSINESS_PATH).catch(() => null)
    const result = await api<{ row: SeveralJobsSwitch }>(PATH)
    if (gen !== routeGen || sequence !== requestSequence) return
    severalJobs = result.row
    if (drawnWhole) {
      // Read again after a save: every answer first, then one draw, so the page keeps its place. Meanwhile the cards
      // on show stay where they are with their controls off: none of them can be pressed on what it said before.
      for (const kind of ['button', 'input', 'textarea']) for (const c of root.querySelectorAll<HTMLButtonElement>(kind)) c.disabled = true
      const [quotes, wa, phone, invoices, tiers, parking, alone] = await Promise.all([quotesRequest, waRequest, phoneRequest, invoicesRequest, tiersRequest, parkingRequest, aloneRequest])
      if (gen !== routeGen || sequence !== requestSequence) return
      quotesSwitch = quotes?.row ?? null
      waCard = wa?.row ?? null
      phoneRow = phone?.row ?? null
      invoiceSetup = invoices?.row ?? null
      tiersSetup = tiers?.row ?? null
      parkingSwitch = parking ? { on: parking.settings.parking_notes === true, version: parking.version } : null
      aloneSwitch = alone?.row ?? null
      render(result.row, root, note, pop)
      // Arrived from Set up invoices while the Invoices card could not be read: brought to it once it is there.
      if (showPending && invoiceSetup) { showPending = false; showInvoicesCard(root) }
      return
    }
    quotesSwitch = null
    phoneRow = null
    waCard = null
    invoiceSetup = null
    tiersSetup = null
    aloneSwitch = null
    parkingSwitch = null
    render(result.row, root, note, pop)
    const quotes = await quotesRequest
    if (gen !== routeGen || sequence !== requestSequence) return
    if (quotes) {
      quotesSwitch = quotes.row
      render(result.row, root, note, pop)
    }
    const wa = await waRequest
    if (gen !== routeGen || sequence !== requestSequence) return
    if (wa) {
      waCard = wa.row
      render(result.row, root, note, pop)
    }
    const phone = await phoneRequest
    if (gen !== routeGen || sequence !== requestSequence) return
    if (phone) {
      phoneRow = phone.row
      render(result.row, root, note, pop)
    }
    const invoices = await invoicesRequest
    if (gen !== routeGen || sequence !== requestSequence) return
    if (invoices) {
      invoiceSetup = invoices.row
      render(result.row, root, note, pop)
    }
    const tiers = await tiersRequest
    if (gen !== routeGen || sequence !== requestSequence) return
    if (tiers) {
      tiersSetup = tiers.row
      render(result.row, root, note, pop)
    }
    const parking = await parkingRequest
    if (gen !== routeGen || sequence !== requestSequence) return
    if (parking) {
      parkingSwitch = { on: parking.settings.parking_notes === true, version: parking.version }
      render(result.row, root, note, pop)
    }
    const alone = await aloneRequest
    if (gen !== routeGen || sequence !== requestSequence) return
    if (alone) {
      aloneSwitch = alone.row
      render(result.row, root, note, pop)
    }
    drawnWhole = true
    // Once the page has drawn everything (each answer above redraws it), brought to the Invoices card.
    if (showPending && invoiceSetup) { showPending = false; showInvoicesCard(root) }
  } catch (err) {
    if (gen !== routeGen || sequence !== requestSequence) return
    clear(root)
    loadError(root, (err as Error).message, () => void load(), undefined, 'Extra features')
  }
}

export function mountExtraFeatures(): void {
  drawnWhole = false
  invoicesSaved = false
  invoicesGlow = false
  arrivedFor = /[?&]show=invoices\b/.test(window.location.hash) ? 'invoices' : null
  showPending = arrivedFor !== null
  if (arrivedFor) window.history?.replaceState?.(window.history.state, '', '#/extra-features')
  void load()
}

export function extraFeaturesOnDataChanged(): void {
  if (!busy) void load()
}

function quotesCard(s: QuotesSwitch, root: HTMLElement): HTMLElement {
  // The same shape as Several jobs above it (Liam's rule 1: same controls, same place): the switch top right, what you
  // will see, where it appears. Set up in three steps written out, because it is not used every day (Liam, 9 Oct 2026:
  // "a way to set this up under new features we dont use this very often").
  const card = el('section', { class: `feature-card${s.quotes_by_email ? ' feature-card-on' : ''}`, 'aria-labelledby': 'feature-quotes-email' })
  const sw = switchButton(s.quotes_by_email, false)
  sw.setAttribute('aria-labelledby', 'feature-quotes-email')
  card.append(el('div', { class: 'feature-top' }, featureIcon('feature-icon', 'mail'),
    el('div', { class: 'feature-titles' }, el('h3', { id: 'feature-quotes-email' }, 'Quotes by email'),
      el('p', {}, "Customers' quote emails land on New jobs. Your quote goes from Roundly by email, and their reply comes back into Roundly.")),
    el('div', { class: 'feature-control' }, sw, el('span', { class: 'feature-status' }, s.quotes_by_email ? 'On' : 'Off'))))
  const note = el('p', { class: 'form-error', role: 'alert' })
  const pricesButton = (): HTMLButtonElement => {
    const go = el('button', { type: 'button', class: 'btn-primary' }, 'Set your prices') as HTMLButtonElement
    go.addEventListener('click', () => { window.location.hash = '#/prices' })
    return go
  }
  const prices = (): void => {
    clear(note)
    note.append(el('span', {}, 'First, set your prices. '), pricesButton())
  }
  const preview = el('div', { class: 'feature-preview' })
  if (s.quotes_by_email) {
    const copy = el('button', { type: 'button', class: 'btn-secondary' }, 'Copy') as HTMLButtonElement
    copy.addEventListener('click', async () => {
      await copyText(s.quotes_address ?? '', { button: copy, what: 'address' })
    })
    preview.append(el('div', { class: 'quotes-address' }, el('h4', {}, 'Your quotes address'),
      el('p', { class: 'quotes-address-value' }, s.quotes_address ?? ''), copy),
      el('ul', { class: 'quotes-steps' },
        el('li', {}, "Forward quote enquiries to it from your email, or put it on your website's contact form."),
        el('li', {}, 'Each one lands on New jobs. Nothing is sent to the customer until you send it.'),
        el('li', {}, 'When a customer replies to your quote, the reply lands on their card in New jobs, and in Needs you on the Diary.'),
        el('li', {}, 'Receiving enquiries is free.')))
  } else {
    preview.append(el('p', { class: 'feature-see' }, el('strong', {}, 'To set it up:')),
      el('ol', { class: 'quotes-steps' },
        el('li', {}, s.has_prices ? 'Your prices are set. ✓' : 'Set your prices, so a quote can be worked out.'),
        el('li', {}, 'Turn it on: Roundly gives your round its own email address.'),
        el('li', {}, "Forward quote enquiries to that address from your email, or put it on your website's contact form.")))
    if (!s.has_prices) preview.append(pricesButton())
  }
  card.append(preview)
  card.append(el('div', { class: 'feature-where' }, el('strong', {}, 'Where it appears'),
    el('span', {}, 'New jobs (each emailed enquiry and its replies), Needs you on the Diary (Waiting for your OK, Replied to your quote), and Send by email in Write a quote.')))
  sw.addEventListener('click', async () => {
    if (stillSaving(SETTINGS_PATHS, note)) return
    if (!s.quotes_by_email && !s.has_prices) { prices(); return }
    const gen = routeGen
    busy = true
    slide(sw, !s.quotes_by_email)
    const buttons = Array.from(root.querySelectorAll<HTMLButtonElement>('button')).map(b => ({ b, disabled: b.disabled }))
    for (const { b } of buttons) b.disabled = true
    const from = s.version
    const ack = await mutate<QuotesSwitch>({ label: s.quotes_by_email ? 'Turn off quotes by email' : 'Turn on quotes by email', method: 'PATCH', path: QUOTES_PATH, body: { on: !s.quotes_by_email, version: from } })
    busy = false
    if (ack.ok) { carrySettingsVersion(from, ack.row); void loadMe() }
    if (ack.status >= 400 && ack.status < 500) for (const q of getQueue()) if ((q.status === 'conflict' || q.status === 'failed') && q.path === QUOTES_PATH) dismiss(q.key)
    if (gen !== routeGen) return
    if (ack.ok || ack.status === 409 && ack.code !== 'prices_needed') { void load(); return }
    for (const { b, disabled } of buttons) b.disabled = disabled
    slide(sw, s.quotes_by_email)
    if (ack.code === 'prices_needed') prices()
    else note.textContent = failureWords(ack)
  })
  if (s.quotes_by_email) card.append(el('p', { class: 'muted' }, 'Turning it off stops straight away. Cards already on New jobs stay.'))
  card.append(note)
  return card
}

// Invoices to customers (0065). The same shape as the cards above: the switch top right, what it does, where it appears.
// Its set-up shows only once it is on (Liam: set-up only when switched on). VAT is off as standard.
function invoicesCard(s: InvoiceSetup, root: HTMLElement): HTMLElement {
  const card = el('section', { class: `feature-card${s.on ? ' feature-card-on' : ''}`, 'aria-labelledby': 'feature-invoices' })
  const sw = switchButton(s.on, false)
  sw.setAttribute('aria-labelledby', 'feature-invoices')
  // Arrived from "Set up invoices": its switch glows until it is on, whatever redraws the page meanwhile.
  if (arrivedFor === 'invoices' && !s.on) sw.classList.add('guide-glow')
  card.append(el('div', { class: 'feature-top' }, featureIcon('feature-icon', 'mail'),
    el('div', { class: 'feature-titles' }, el('h3', { id: 'feature-invoices' }, 'Invoices'),
      el('p', {}, 'Send a customer an invoice for a visit, or one a month for a business customer. A paid invoice works as their receipt.')),
    el('div', { class: 'feature-control' }, sw, el('span', { class: 'feature-status' }, s.on ? 'On' : 'Off'))))
  const note = el('p', { class: 'form-error', role: 'alert' })
  if (s.on) {
    const form = el('div', { class: 'invoice-setup' })
    const field = (id: string, label: string, value: string, opts: { area?: boolean; hint?: string; type?: string } = {}): HTMLInputElement | HTMLTextAreaElement => {
      const input = (opts.area ? el('textarea', { id, rows: '3' }) : el('input', { id, type: opts.type ?? 'text', ...(opts.hint ? { placeholder: opts.hint } : {}) })) as HTMLInputElement | HTMLTextAreaElement
      input.value = value
      form.append(el('label', { for: id }, label), input)
      return input
    }
    const address = field('inv-set-address', 'Business address', s.address ?? '', { area: true })
    const contact = field('inv-set-contact', 'Phone or email on the invoice', s.contact ?? '', { hint: '07700 900000 · you@example.com' })
    const logoRow = el('div', { class: 'day-close-logo-row' })
    const drawLogo = (has: boolean): void => {
      clear(logoRow)
      logoRow.append(has ? el('img', { src: `/api/business/invoice-logo?v=${Date.now()}`, alt: 'Your logo', class: 'invoice-logo' }) : el('div', { class: 'invoice-logo invoice-logo-none' }, 'Logo'))
      const pick = el('input', { type: 'file', accept: 'image/*', class: 'day-close-file', 'aria-label': 'Choose a logo' }) as HTMLInputElement
      const add = el('button', { type: 'button', class: 'btn-secondary' }, has ? 'Change logo' : 'Add logo')
      add.addEventListener('click', () => pick.click())
      pick.addEventListener('change', () => {
        const f = pick.files?.[0]
        if (!f) return
        void shrink(f).then((blob) => blob && mutateUpload({ path: '/api/business/invoice-logo', key: crypto.randomUUID(), fields: {}, file: blob, filename: 'logo.jpg', fileField: 'image', ackField: 'row' }))
          .then((ack) => { if (ack && ack.ok) drawLogo(true); else note.textContent = 'The logo did not save. Try again.' })
      })
      logoRow.append(add, pick)
    }
    drawLogo(s.has_logo)
    form.append(el('label', {}, 'Logo'), logoRow)
    const days = field('inv-set-days', 'Pay within (days)', String(s.pay_days), { type: 'number' })
    const two = el('div', { class: 'invoice-two' })
    const prefix = el('input', { id: 'inv-set-prefix', value: s.prefix, 'aria-label': 'Invoice numbers start with', maxlength: '6' }) as HTMLInputElement
    const next = el('input', { id: 'inv-set-next', type: 'number', value: String(s.next), 'aria-label': 'Next invoice number', min: String(s.next) }) as HTMLInputElement
    two.append(prefix, next)
    form.append(el('label', { for: 'inv-set-prefix' }, `Numbers (the next is ${s.next_number})`), two)
    let vatOn = s.vat_on
    let vatNumber: HTMLInputElement | null = null
    let vatRate: HTMLInputElement | null = null
    if (s.vat_allowed) {
      const vat = el('div', { class: 'invoice-vat' })
      const box = el('input', { id: 'inv-set-vat', type: 'checkbox' }) as HTMLInputElement
      box.checked = vatOn
      const extra = el('div', { class: 'invoice-setup' })
      const drawVat = (): void => {
        clear(extra)
        if (!vatOn) return
        vatNumber = el('input', { id: 'inv-set-vat-number', value: s.vat_number ?? '', placeholder: 'GB123456789' }) as HTMLInputElement
        vatRate = el('input', { id: 'inv-set-vat-rate', type: 'number', value: String(s.vat_rate_bp / 100), step: '0.5' }) as HTMLInputElement
        extra.append(el('label', { for: 'inv-set-vat-number' }, 'VAT number'), vatNumber, el('label', { for: 'inv-set-vat-rate' }, 'VAT rate (%)'), vatRate,
          el('p', { class: 'muted' }, 'Your prices include VAT: each invoice shows the VAT part of its total.'))
      }
      box.addEventListener('change', () => { vatOn = box.checked; drawVat() })
      drawVat()
      vat.append(el('label', { for: 'inv-set-vat', class: 'invoice-vat-switch' }, box, ' Charge VAT (off unless you are VAT registered)'), extra)
      form.append(vat)
    }
    if (!s.has_bank) form.append(el('p', { class: 'muted' }, 'Add your bank details in Your business, so each invoice says how to pay.'))
    if (!s.email_on) form.append(el('p', { class: 'muted' }, 'To email invoices from Roundly, turn on Quotes by email. Without it, invoices go by WhatsApp, text or a link.'))
    const save = el('button', { type: 'button', class: 'btn-primary' }, 'Save invoice details') as HTMLButtonElement
    save.addEventListener('click', async () => {
      if (stillSaving(SETTINGS_PATHS, note)) return
      const body: Record<string, unknown> = { version: s.version, address: address.value, contact: contact.value }
      if (prefix.value.trim() !== '') body.prefix = prefix.value.trim().toUpperCase()
      // A box left empty keeps what was there (an empty "Pay within" is not 0 days, review).
      if (days.value.trim() !== '') body.pay_days = Number(days.value)
      if (next.value.trim() !== '' && Number(next.value) !== s.next) body.next = Number(next.value)
      if (s.vat_allowed) {
        body.vat_on = vatOn
        if (vatOn) { body.vat_number = vatNumber?.value ?? ''; if ((vatRate?.value ?? '').trim() !== '') body.vat_rate_bp = Math.round(Number(vatRate!.value) * 100) }
      }
      const gen = routeGen
      save.disabled = true
      busy = true
      const ack = await mutate<InvoiceSetup>({ label: 'Invoice details', method: 'PATCH', path: INVOICES_PATH, body })
      busy = false
      save.disabled = false
      if (ack.ok) carrySettingsVersion(body.version, ack.row)
      // No signal: kept on the phone, said as Your business says it; the card shows Saved once it has really gone.
      if (ack.status === 0) toast('Saved on this phone. It sends when you have signal.')
      // Left the page meanwhile: nothing is drawn over the screen the owner is on now.
      if (gen !== routeGen) return
      if (ack.ok) { invoicesSaved = true; invoicesGlow = true }
      if (ack.ok || ack.status === 0) { note.textContent = ''; void load(); return }
      note.textContent = failureWords(ack)
    })
    form.append(save)
    if (invoicesSaved) {
      save.disabled = true
      save.className = 'btn-secondary invoice-saved'
      clear(save)
      save.append(lineDrawing('tick', 20), 'Saved')
      const said = el('p', { class: 'muted invoice-saved-line' }, 'Your next invoice uses these details.')
      const next = invoiceNext(s)
      form.append(said, next)
      const back = (): void => {
        if (!invoicesSaved) return
        invoicesSaved = false
        save.disabled = false
        save.className = 'btn-primary'
        save.textContent = 'Save invoice details'
        said.remove(); next.remove()
      }
      form.addEventListener('input', back)
      form.addEventListener('change', back)
    }
    card.append(form)
  }
  if (invoicesGlow) { invoicesGlow = false; card.classList.add('rd-land') }
  card.append(el('div', { class: 'feature-where' }, el('strong', {}, 'Where it appears'),
    el('span', {}, 'Invoice on a done visit\'s details, Invoices on each customer\'s page, and invoices waiting to be paid on Owed.')))
  sw.addEventListener('click', async () => {
    if (stillSaving(SETTINGS_PATHS, note)) return
    const gen = routeGen
    busy = true
    slide(sw, !s.on)
    const from = s.version
    const ack = await mutate<InvoiceSetup>({ label: s.on ? 'Turn off invoices' : 'Turn on invoices', method: 'PATCH', path: INVOICES_PATH, body: { on: !s.on, version: from } })
    busy = false
    if (ack.ok) { carrySettingsVersion(from, ack.row); setInvoicesOn(!s.on); void loadMe() }
    if (ack.status >= 400 && ack.status < 500) for (const q of getQueue()) if ((q.status === 'conflict' || q.status === 'failed') && q.path === INVOICES_PATH) dismiss(q.key)
    if (gen !== routeGen) return
    if (ack.ok || (ack.status === 409 && ack.code !== 'invoices_open')) { void load(); return }
    slide(sw, s.on)
    note.textContent = ack.code === 'invoices_open' ? (ack.error ?? 'Some invoices are still to be paid.') : failureWords(ack)
  })
  if (s.on) card.append(el('p', { class: 'muted' }, 'Turning it off hides the Invoice buttons. Invoices already sent stay, and their links keep working.'))
  card.append(note)
  return card
}

// What next?, under Saved (design B): two choice tiles, the kit's Picture tiles. Make an invoice opens the New invoice
// pop-up on Invoices (#/invoices?new=1); See how it looks shows these details on a made-up customer, sending nothing.
function invoiceNext(s: InvoiceSetup): HTMLElement {
  const words = (title: string, hint: string): HTMLElement => el('span', { class: 'invoice-next-words' }, el('strong', {}, title), el('span', {}, hint))
  const make = el('a', { href: '#/invoices?new=1', class: 'invoice-next-tile invoice-next-go' }, lineDrawing('invoiceAdd', 26), words('Make an invoice', 'Pick who and which jobs'))
  const look = el('button', { type: 'button', class: 'invoice-next-tile' }, lineDrawing('eye', 26), words('See how it looks', 'Your details on a sample'))
  look.addEventListener('click', () => showSample(s))
  return el('div', { class: 'invoice-next' }, el('h4', {}, 'What next?'), el('div', { class: 'invoice-next-tiles' }, make, look))
}

function showSample(s: InvoiceSetup): void {
  const today = todayISO()
  const due = new Date(`${today}T12:00:00`)
  due.setDate(due.getDate() + s.pay_days)
  const pence = 2000
  const vatBp = s.vat_on ? s.vat_rate_bp : null
  const biz = hasBusinessSettings() ? businessSettings() : null
  const day = new Date(`${today}T12:00:00`).toLocaleDateString(screenLocale(), { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' })
  const view = customerInvoiceView({
    customer_id: 0, number: s.next_number, kind: 'visit', period: null, issued_on: today,
    due_on: `${due.getFullYear()}-${String(due.getMonth() + 1).padStart(2, '0')}-${String(due.getDate()).padStart(2, '0')}`,
    subtotal_pence: pence, less_pence: 0, total_pence: pence,
    vat_rate_bp: vatBp, vat_pence: vatBp ? Math.round(pence - pence / (1 + vatBp / 10000)) : null,
    from: { name: biz?.business_name ?? '', address: s.address, contact: s.contact, bank: biz?.bank_lines ?? null, vat_number: s.vat_on ? s.vat_number : null },
    to: { name: 'A customer', address: '1 Sample Road' },
    lines: [{ clean_id: 0, date: today, label: `${workTitle()}, ${day}`, pence }],
  }, s.has_logo ? `/api/business/invoice-logo?v=${Date.now()}` : null)
  const sh = sheet('How your invoice looks')
  sh.head.append(el('h2', {}, 'How your invoice looks'))
  sh.body.append(el('p', { class: 'muted' }, 'A made-up customer and price, so you can check your details. Nothing is sent.'), view)
}

// Tiers from job time (0070; Liam, 13 Oct 2026: "Yes to tier setting. Tier one is best", "so that people could put their
// own tier system into it"). The same shape as the cards above: the switch top right, what it does, where it appears;
// the cut-offs only once it is on. Each cut-off is pounds a minute of outside work, highest first, two to four tiers.
function tiersCard(s: TiersSetup, root: HTMLElement): HTMLElement {
  const on = s.cutoffs !== null
  const card = el('section', { class: `feature-card${on ? ' feature-card-on' : ''}`, 'aria-labelledby': 'feature-tiers' })
  const sw = switchButton(on, false)
  sw.setAttribute('aria-labelledby', 'feature-tiers')
  card.append(el('div', { class: 'feature-top' }, featureIcon('feature-icon'),
    el('div', { class: 'feature-titles' }, el('h3', { id: 'feature-tiers' }, 'Tiers from job time'),
      el('p', {}, "Each customer's tier fills in from their outside price and how long the outsides take. Tier 1 is your best.")),
    el('div', { class: 'feature-control' }, sw, el('span', { class: 'feature-status' }, on ? 'On' : 'Off'))))
  const note = el('p', { class: 'form-error', role: 'alert' })
  const save = async (cutoffs: number[] | null, label: string): Promise<boolean> => {
    if (stillSaving([TIERS_PATH], note)) return false
    const gen = routeGen
    busy = true
    const buttons = Array.from(root.querySelectorAll<HTMLButtonElement>('button')).map(b => ({ b, disabled: b.disabled }))
    for (const { b } of buttons) b.disabled = true
    const ack = await mutate<TiersSetup>({ label, method: 'PATCH', path: TIERS_PATH, body: { cutoffs, version: s.version } })
    busy = false
    if (ack.status >= 400 && ack.status < 500) for (const q of getQueue()) if ((q.status === 'conflict' || q.status === 'failed') && q.path === TIERS_PATH) dismiss(q.key)
    if (gen !== routeGen) return true
    if (ack.ok || ack.status === 409) { void load(); return true }
    for (const { b, disabled } of buttons) b.disabled = disabled
    slide(sw, on)
    note.textContent = failureWords(ack)
    return true
  }
  const preview = el('div', { class: 'feature-preview' })
  if (!on) {
    // Wording reviewed with Astra (13 Oct 2026, Liam: "Discuss the wording with astra").
    preview.append(el('p', { class: 'feature-see' }, el('strong', {}, "What you'll see: "), 'Tier 1, 2, 3 or 4 on each customer after a job where you tapped Start and Finish.'),
      el('ul', { class: 'quotes-steps' },
        el('li', {}, 'Uses up to their last three jobs with Start and Finish tapped. The outside price is divided by the time for the outsides.'),
        el('li', {}, "When you do the insides too, the usual time for that customer's outsides counts as outsides and the rest as insides. Until their outsides have been timed on their own, the time is split equally."),
        el('li', {}, 'You set the amount a minute for each tier. A tier you choose yourself stays the same.')))
  } else {
    const cutoffs = [...(s.cutoffs as number[])]
    const rows = el('div', { class: 'tier-rows' })
    const inputs: HTMLInputElement[] = []
    const draw = (): void => {
      clear(rows)
      inputs.length = 0
      cutoffs.forEach((pence, i) => {
        const input = el('input', { inputmode: 'decimal', class: 'tier-input', 'aria-label': `Tier ${i + 1}, ${currencyWord()} a minute or more` }) as HTMLInputElement
        input.value = (pence / 100).toFixed(2)
        inputs.push(input)
        rows.append(el('label', { class: 'tier-row' }, el('strong', {}, `Tier ${i + 1}`), el('span', {}, currencySymbol()), input, el('span', {}, 'a minute or more')))
      })
      const last = cutoffs.at(-1) as number
      rows.append(el('p', { class: 'tier-row tier-row-last' }, el('strong', {}, `Tier ${cutoffs.length + 1}`), el('span', {}, `less than ${money(last)} a minute`)))
      const acts = el('div', { class: 'tier-acts' })
      if (cutoffs.length < 3) {
        const add = el('button', { type: 'button', class: 'link-button' }, 'Add a tier')
        add.addEventListener('click', () => { read(); cutoffs.push(Math.max(1, Math.round((cutoffs.at(-1) as number) * 0.75))); draw() })
        acts.append(add)
      }
      if (cutoffs.length > 1) {
        const remove = el('button', { type: 'button', class: 'link-button' }, `Remove Tier ${cutoffs.length + 1}`)
        remove.addEventListener('click', () => { read(); cutoffs.pop(); draw() })
        acts.append(remove)
      }
      rows.append(acts)
    }
    // What is typed so far, kept when a tier is added or removed.
    const read = (): void => { inputs.forEach((input, i) => { try { const p = parseMoney(input.value); if (p !== null && p > 0) cutoffs[i] = p } catch { /* kept as it was */ } }) }
    draw()
    const go = el('button', { type: 'button', class: 'btn-primary' }, 'Save tiers') as HTMLButtonElement
    go.addEventListener('click', () => {
      note.textContent = ''
      const typed: number[] = []
      for (const [i, input] of inputs.entries()) {
        let p: number | null = null
        try { p = parseMoney(input.value) } catch { /* below */ }
        if (p === null || p <= 0) { note.textContent = `Put in an amount for Tier ${i + 1}, for example 1.20.`; input.focus(); return }
        if (i > 0 && p >= typed[i - 1]) { note.textContent = `Tier ${i + 1} needs a lower amount a minute than Tier ${i}.`; input.focus(); return }
        typed.push(p)
      }
      void save(typed, 'Save tiers')
    })
    preview.append(rows, go,
      el('p', { class: 'muted' }, s.figured === 0
        ? 'No customer has a job with Start and Finish tapped yet. Their tier fills in after their first one.'
        : `Tiers filled in for ${s.figured} ${s.figured === 1 ? 'customer' : 'customers'} so far${s.by_hand ? `, ${s.by_hand} of them with a tier you chose yourself, which stays the same` : ''}.`),
      el('ul', { class: 'quotes-steps' },
        el('li', {}, 'Uses up to their last three jobs with Start and Finish tapped. The outside price is divided by the time for the outsides.'),
        el('li', {}, "When you do the insides too, the usual time for that customer's outsides counts as outsides and the rest as insides. Until their outsides have been timed on their own, the time is split equally."),
        el('li', {}, 'A tier you choose yourself stays the same. Clear it on the customer to let job times fill it in.')))
  }
  card.append(preview)
  card.append(el('div', { class: 'feature-where' }, el('strong', {}, 'Where it appears'),
    el('span', {}, 'On each customer\'s page, in Work, and when you sort Messages by tier.')))
  sw.addEventListener('click', () => {
    slide(sw, !on)
    void save(on ? null : STARTING_CUTOFFS, on ? 'Turn off tiers' : 'Turn on tiers').then((went) => { if (!went) slide(sw, on) })
  })
  if (on) card.append(el('p', { class: 'muted' }, 'Turning it off keeps every tier as it is now.'))
  card.append(note)
  return card
}

// Working with others (0078). The same shape as the cards above: the switch top right, what it does, where it appears.
// On is a round with a team; Off is just the owner. While the round is not one person (someone else signs in, the owner
// has no name on their visits, or coming work is in someone else's name) it cannot go off: the switch is locked and the
// server's reason says what to do first. Either way asks first, in a sheet over the card; nothing is sent until yes.
// Parking notes: a plain switch, no question asked (nothing changes but one link on Today, and it goes when switched off).
function parkingCard(s: ParkingSwitch, root: HTMLElement): HTMLElement {
  const card = el('section', { class: `feature-card${s.on ? ' feature-card-on' : ''}`, 'aria-labelledby': 'feature-parking' })
  const sw = switchButton(s.on, false)
  sw.setAttribute('aria-labelledby', 'feature-parking')
  card.append(el('div', { class: 'feature-top' }, featureIcon('feature-icon', 'switch'),
    el('div', { class: 'feature-titles' }, el('h3', { id: 'feature-parking' }, 'Best times and calling notes'),
      el('p', {}, "Add a customer's best times or calling notes from the job, when you find out at the door. Parking is always in a job's ⋯ (Parking…).")),
    el('div', { class: 'feature-control' }, sw, el('span', { class: 'feature-status' }, s.on ? 'On' : 'Off'))))
  card.append(el('div', { class: 'feature-preview' }, el('p', { class: 'feature-see' }, el('strong', {}, s.on ? 'With it on: ' : 'With it off: '),
    s.on ? '"+ Add parking or times" under Access & notes on your jobs in Today.' : 'Parking is still on each customer\'s page, under At the door.')))
  card.append(el('div', { class: 'feature-where' }, el('strong', {}, 'Where it appears'),
    el('span', {}, 'Today, on your own jobs. What you add shows on the customer\'s page, the Diary and your workers\' Today.')))
  const note = el('p', { class: 'form-error', role: 'alert' })
  sw.addEventListener('click', () => {
    if (stillSaving(SETTINGS_PATHS, note)) return
    note.textContent = ''
    slide(sw, !s.on)
    void flipParking(s, !s.on, root, sw, note)
  })
  card.append(note)
  return card
}

async function flipParking(s: ParkingSwitch, on: boolean, root: HTMLElement, sw: HTMLElement, note: HTMLElement): Promise<void> {
  if (busy) return
  const gen = routeGen
  busy = true
  const from = s.version
  const ack = await mutate<{ version: number }>({ label: on ? 'Turn on parking notes' : 'Turn off parking notes', method: 'PATCH', path: BUSINESS_PATH, body: { parking_notes: on, version: from } })
  busy = false
  if (ack.ok) {
    carrySettingsVersion(from, ack.row)
    // The settings in memory follow at once, so Today draws the link (or takes it away) without a reload.
    applySavedBusinessRow(ack.row)
  }
  if (gen !== routeGen) return
  if (ack.ok || ack.status === 409) { void load(); return }
  if (ack.status === 0) { note.textContent = 'No signal. It switches when your phone is back online.'; return }
  slide(sw, !on)
  note.textContent = failureWords(ack)
}

function aloneCard(s: AloneSwitch, root: HTMLElement): HTMLElement {
  const on = !s.works_alone
  const locked = on && !s.can_go_alone
  const card = el('section', { class: `feature-card${on ? ' feature-card-on' : ''}`, 'aria-labelledby': 'feature-with-others' })
  const sw = switchButton(on, locked)
  sw.setAttribute('aria-labelledby', 'feature-with-others')
  if (locked) sw.setAttribute('aria-describedby', 'feature-alone-why')
  card.append(el('div', { class: 'feature-top' }, featureIcon('feature-icon', 'people'),
    el('div', { class: 'feature-titles' }, el('h3', { id: 'feature-with-others' }, 'Working with others'),
      el('p', {}, 'Who is out each day, who does each visit, and worker pay. Turn it off when it is just you on the round.')),
    el('div', { class: 'feature-control' }, sw, el('span', { class: 'feature-status' }, on ? 'On' : 'Off'))))
  card.append(el('div', { class: 'feature-preview' }, el('p', { class: 'feature-see' }, el('strong', {}, on ? 'With it on: ' : 'With it off: '),
    on ? 'names on days and visits, and "Who\'s doing it?" when you book a visit.' : 'your days come from My working days, with no names on days or visits.')))
  card.append(el('div', { class: 'feature-where' }, el('strong', {}, 'Where it appears'),
    el('span', {}, 'The Diary, Book a visit, Today, the days Messages offers, and Money.'),
    ...(on ? [] : [el('br'), el('a', { href: '#/my-days' }, 'Open My working days ›')])))
  if (locked) card.append(el('p', { class: 'feature-stays', id: 'feature-alone-why' }, lockIcon(), el('span', {}, s.not_alone_because ?? "It isn't just you on this round yet.")))
  const note = el('p', { class: 'form-error', role: 'alert' })
  if (!locked) {
    sw.addEventListener('click', () => {
      if (stillSaving(SETTINGS_PATHS, note)) return
      if (asking) return
      asking = true
      note.textContent = ''
      // It moves under the finger, and the confirm rises once the knob has landed, as for several jobs.
      slide(sw, !on)
      const still = typeof window.matchMedia === 'function' && window.matchMedia('(prefers-reduced-motion: reduce)').matches
      window.setTimeout(() => {
        confirmTurnOn(
          () => { asking = false; void flipAlone(s, on, root, sw, note) },
          () => { asking = false; slide(sw, on) },
          card,
          on ? { title: ALONE_TITLE, words: ALONE_WORDS, yes: 'Yes, just me', icon: 'people' } : { title: WITH_OTHERS_TITLE, words: WITH_OTHERS_WORDS, yes: 'Turn on', icon: 'people' },
        )
      }, still ? 0 : SLIDE_MS)
    })
  }
  card.append(note)
  return card
}

// alone: the round goes to one person (Working with others off). A refusal because it is not one person after all says
// the server's reason on the card; any other refusal reads the page again.
async function flipAlone(s: AloneSwitch, alone: boolean, root: HTMLElement, sw: HTMLElement, note: HTMLElement): Promise<void> {
  if (busy) return
  const gen = routeGen
  busy = true
  const buttons = Array.from(root.querySelectorAll<HTMLButtonElement>('button')).map(b => ({ b, disabled: b.disabled }))
  for (const { b } of buttons) b.disabled = true
  const from = s.version
  const body: Record<string, unknown> = { alone, version: from }
  if (alone) body.confirmed = true
  const ack = await mutate<AloneSwitch>({ label: alone ? 'Just you on this round' : 'Turn on working with others', method: 'PATCH', path: ALONE_PATH, body })
  busy = false
  // The rest of the app learns it from /api/me and draws its screens again (me.ts), wherever the owner is now.
  if (ack.ok) { carrySettingsVersion(from, ack.row); window.setTimeout(() => void loadMe(), POP_MS) }
  if (ack.status >= 400 && ack.status < 500) for (const q of getQueue()) if ((q.status === 'conflict' || q.status === 'failed') && q.path === ALONE_PATH) dismiss(q.key)
  if (gen !== routeGen) return
  if (ack.ok || (ack.status === 409 && ack.code !== 'not_alone')) { void load(); return }
  for (const { b, disabled } of buttons) b.disabled = disabled
  slide(sw, alone) // back where it was: on (with others) when the tap was to go alone
  note.textContent = ack.code === 'not_alone' ? (ack.error ?? "It isn't just you on this round yet.") : failureWords(ack)
}
